import { LDClient, initialize } from 'launchdarkly-js-client-sdk';
import { useEffect, useState } from 'react';
import { useUser } from 'reactfire';

export const ATTAIN_HQ_DENTSU_OTS_EXPORT_FLAG =
  'uiAudienceManagerModeledOtsExportDentsuVisible';

export const ATTAIN_HQ_DENTSU_OTS_SEED_EXPORT_FLAG =
  'uiAudienceManagerSeedOtsExportDentsuVisible';

let attainHqLdClientInitPromise: Promise<LDClient | null> | null = null;
let attainHqIdentifiedUserKey: string | null = null;

const getAttainHqClientSideId = (): string | undefined =>
  process.env.ATTAIN_HQ_LAUNCHDARKLY_PROJECT_CLIENT_SIDE_ID;

/**
 * Singleton Attain HQ LaunchDarkly client (separate from the primary LD provider).
 */
const getAttainHqLdClient = async (
  userKey: string,
  email?: string | null
): Promise<LDClient | null> => {
  const clientSideID = getAttainHqClientSideId();
  if (!clientSideID) {
    return null;
  }

  if (!attainHqLdClientInitPromise) {
    attainHqLdClientInitPromise = (async () => {
      try {
        const client = initialize(clientSideID, {
          key: userKey,
          email: email || undefined,
        });
        await client.waitForInitialization();
        attainHqIdentifiedUserKey = userKey;
        return client;
      } catch (error) {
        console.error('Attain HQ LaunchDarkly initialization failed:', error);
        attainHqLdClientInitPromise = null;
        return null;
      }
    })();
  }

  const client = await attainHqLdClientInitPromise;
  if (!client) {
    return null;
  }

  if (attainHqIdentifiedUserKey !== userKey) {
    await client.identify({
      key: userKey,
      email: email || undefined,
    });
    attainHqIdentifiedUserKey = userKey;
  }

  return client;
};

const useAttainHqDentsuFlag = (flagKey: string): boolean => {
  const { data: user } = useUser();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!user?.uid) {
        if (!cancelled) {
          setVisible(false);
        }
        return;
      }

      const client = await getAttainHqLdClient(user.uid, user.email);
      if (!client || cancelled) {
        return;
      }

      try {
        const flagValue = client.variation(flagKey, false);
        if (!cancelled) {
          setVisible(Boolean(flagValue));
        }
      } catch (error) {
        console.error(`Failed to evaluate ${flagKey}:`, error);
        if (!cancelled) {
          setVisible(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid, user?.email, flagKey]);

  return visible;
};

/**
 * Evaluates Dentsu OTS modeled export visibility against the Attain HQ LD project.
 */
export const useAttainHqDentsuExportFlag = (): boolean =>
  useAttainHqDentsuFlag(ATTAIN_HQ_DENTSU_OTS_EXPORT_FLAG);

/**
 * Evaluates Dentsu OTS seed export visibility against the Attain HQ LD project.
 */
export const useAttainHqDentsuSeedExportFlag = (): boolean =>
  useAttainHqDentsuFlag(ATTAIN_HQ_DENTSU_OTS_SEED_EXPORT_FLAG);
